import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { 
  Search, 
  Filter, 
  Loader2, 
  Eye,
  CheckCircle,
  Calendar,
  Ticket,
  Package,
  Users
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_TYPES = [
  { value: "all", label: "All Bookings", icon: Calendar },
  { value: "class", label: "Class Bookings", icon: Users, prefix: "EDK" },
  { value: "event", label: "Event Tickets", icon: Ticket, prefix: "EVT" },
  { value: "package", label: "Package Purchases", icon: Package, prefix: "PKG" },
];

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bookingTypeFilter, setBookingTypeFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [stats, setStats] = useState({});

  const PAGE_SIZE = 10;

  useEffect(() => {
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, bookingTypeFilter, page]);

  // Tab counts only need to load once — not on every filter/page change
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchBookings = async (pageToFetch) => {
    const currentPage = pageToFetch || page;
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (bookingTypeFilter !== "all") params.booking_type = bookingTypeFilter;
      if (search) params.search = search;
      params.limit = PAGE_SIZE;
      params.skip = (currentPage - 1) * PAGE_SIZE;

      const res = await axios.get(`${API}/admin/bookings`, { params });
      setBookings(res.data.bookings || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, { params: { limit: 1000 } });
      const all = res.data.bookings || [];
      setStats({
        all: all.length,
        class: all.filter(b => b.booking_type === "class").length,
        event: all.filter(b => b.booking_type === "event").length,
        package: all.filter(b => b.booking_type === "package").length,
      });
    } catch (error) {
      console.error("Error fetching booking stats:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchBookings(1);
  };

  const handleCheckIn = (bookingId) => {
    const guestName = selectedBooking?.guest_name || "guest";
    toast(`Check in ${guestName}?`, {
      description: "Mark this guest as arrived.",
      action: {
        label: "Check In",
        onClick: async () => {
          try {
            await axios.put(`${API}/admin/bookings/${bookingId}`, {
              status: "completed",
              checked_in_at: new Date().toISOString()
            });
            toast.success("Guest checked in successfully!");
            fetchBookings();
            setSelectedBooking(null);
          } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to check in");
          }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    confirmed: "bg-green-500/20 text-green-400",
    completed: "bg-blue-500/20 text-blue-400",
    cancelled: "bg-red-500/20 text-red-400",
    no_show: "bg-neutral-500/20 text-neutral-400",
  };

  const paymentColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    paid: "bg-green-500/20 text-green-400",
    failed: "bg-red-500/20 text-red-400",
    refunded: "bg-neutral-500/20 text-neutral-400",
  };

  const getBookingTypeColor = (type) => {
    const colors = {
      class: "bg-blue-500/20 text-blue-400",
      event: "bg-purple-500/20 text-purple-400",
      package: "bg-green-500/20 text-green-400",
      private_session: "bg-empire-gold/20 text-empire-gold",
    };
    return colors[type] || "bg-neutral-500/20 text-neutral-400";
  };

  const renderBookingsTable = () => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-neutral-800">
            <TableHead className="text-neutral-400">Booking Ref</TableHead>
            <TableHead className="text-neutral-400">Guest</TableHead>
            <TableHead className="text-neutral-400">Type</TableHead>
            <TableHead className="text-neutral-400">Date</TableHead>
            <TableHead className="text-neutral-400">Amount</TableHead>
            <TableHead className="text-neutral-400">Status</TableHead>
            <TableHead className="text-neutral-400">Payment</TableHead>
            <TableHead className="text-neutral-400">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((booking) => (
            <TableRow key={booking.id} className="border-neutral-800 hover:bg-neutral-800/50">
              <TableCell className="font-mono text-empire-gold text-sm">
                {booking.booking_number}
              </TableCell>
              <TableCell>
                <div>
                  <p className="text-white font-medium">{booking.guest_name}</p>
                  <p className="text-xs text-neutral-500">{booking.guest_email}</p>
                </div>
              </TableCell>
              <TableCell>
                <Badge className={getBookingTypeColor(booking.booking_type)}>
                  {booking.booking_type?.replace("_", " ")}
                </Badge>
                {booking.item_label && (
                  <p className="text-xs text-neutral-300 mt-1 max-w-[190px] truncate" title={booking.item_label}>
                    {booking.item_label}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-neutral-300 text-sm">
                {booking.created_at && format(parseISO(booking.created_at), "MMM d, yyyy")}
              </TableCell>
              <TableCell className="text-white font-medium">
                KES {booking.total_amount?.toLocaleString()}
              </TableCell>
              <TableCell>
                <Badge className={statusColors[booking.status]}>
                  {booking.status}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge className={paymentColors[booking.payment_status]}>
                  {booking.payment_status}
                </Badge>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedBooking(booking)}
                  className="text-neutral-400 hover:text-white"
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div data-testid="admin-bookings">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Bookings</h1>
        <p className="text-neutral-400">Manage all customer bookings by type</p>
      </div>

      {/* Booking Type Tabs */}
      <Tabs
        value={bookingTypeFilter}
        onValueChange={(value) => {
          setBookingTypeFilter(value);
          setPage(1);
        }}
        className="mb-6"
      >
        <TabsList className="bg-neutral-900 border border-neutral-800 p-1 h-auto flex-wrap">
          {BOOKING_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <TabsTrigger 
                key={type.value} 
                value={type.value}
                className="data-[state=active]:bg-empire-gold data-[state=active]:text-black px-3 py-2"
              >
                <Icon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{type.label}</span>
                <span className="sm:hidden">{type.value === "all" ? "All" : type.prefix}</span>
                <Badge variant="secondary" className="ml-2 bg-neutral-800 text-xs">
                  {stats[type.value] || 0}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="card-empire mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  placeholder="Search by booking ref, name, or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input-empire pl-10"
                  data-testid="search-bookings"
                />
              </div>
              <Button type="submit" className="btn-empire">
                Search
              </Button>
            </form>
            
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px] bg-neutral-900 border-neutral-800">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="card-empire">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center justify-between">
            <span>
              {BOOKING_TYPES.find(t => t.value === bookingTypeFilter)?.label || "All Bookings"}
            </span>
            <Badge variant="secondary">{total} results</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p className="text-neutral-500">No bookings found</p>
            </div>
          ) : (
            <>
              {renderBookingsTable()}
              {/* Pagination summary and controls */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 text-sm text-neutral-400">
                <div>
                  {total > 0 && (
                    <span>
                      Showing{" "}
                      <span className="text-white">
                        {Math.min((page - 1) * PAGE_SIZE + 1, total)}
                      </span>{" "}
                      to{" "}
                      <span className="text-white">
                        {Math.min(page * PAGE_SIZE, total)}
                      </span>{" "}
                      of{" "}
                      <span className="text-white">{total}</span> results
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    className="text-neutral-400 hover:text-white"
                  >
                    Prev
                  </Button>
                  {Array.from({
                    length: Math.min(
                      5,
                      Math.ceil(total / PAGE_SIZE) || 1
                    ),
                  }).map((_, index) => {
                    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
                    let startPage = Math.max(1, page - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    if (endPage - startPage < 4) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    const pageNumber = startPage + index;
                    if (pageNumber > totalPages) return null;

                    const isActive = pageNumber === page;
                    return (
                      <Button
                        key={pageNumber}
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setPage(pageNumber)}
                        className={
                          isActive
                            ? "btn-empire px-3 py-1 h-8"
                            : "text-neutral-400 hover:text-white px-3 py-1 h-8"
                        }
                      >
                        {pageNumber}
                      </Button>
                    );
                  })}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={page >= Math.ceil(total / PAGE_SIZE) || total === 0}
                    onClick={() =>
                      setPage((prev) =>
                        Math.min(prev + 1, Math.ceil(total / PAGE_SIZE) || 1)
                      )
                    }
                    className="text-neutral-400 hover:text-white"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Booking Detail Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-neutral-900 border-neutral-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              Booking Details
              {selectedBooking && (
                <Badge className={getBookingTypeColor(selectedBooking.booking_type)}>
                  {selectedBooking.booking_type?.replace("_", " ")}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="p-3 bg-neutral-800/50 rounded-lg">
                <p className="text-sm text-neutral-500 mb-1">Booking Reference</p>
                <p className="font-mono text-lg text-empire-gold">{selectedBooking.booking_number}</p>
              </div>

              {selectedBooking.item_label && (
                <div className="p-3 bg-neutral-800/50 rounded-lg">
                  <p className="text-sm text-neutral-500 mb-1">
                    {selectedBooking.item_kind === "event"
                      ? "Event"
                      : selectedBooking.item_kind === "package"
                      ? "Package"
                      : "Class"}
                  </p>
                  <p className="text-white font-medium">{selectedBooking.item_label}</p>
                  {selectedBooking.item_datetime_eat && (
                    <p className="text-sm text-neutral-400 mt-1">{selectedBooking.item_datetime_eat}</p>
                  )}
                  {selectedBooking.item_venue && (
                    <p className="text-sm text-neutral-500">{selectedBooking.item_venue}</p>
                  )}
                  {selectedBooking.ticket_quantity > 1 && (
                    <p className="text-sm text-neutral-400 mt-1">
                      {selectedBooking.ticket_quantity} tickets
                    </p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500">Status</p>
                  <Badge className={statusColors[selectedBooking.status]}>
                    {selectedBooking.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Payment</p>
                  <Badge className={paymentColors[selectedBooking.payment_status]}>
                    {selectedBooking.payment_status}
                  </Badge>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-neutral-500 mb-1">Guest Information</p>
                <div className="p-3 bg-neutral-800/50 rounded-lg">
                  <p className="text-white font-medium">{selectedBooking.guest_name}</p>
                  <p className="text-neutral-400 text-sm">{selectedBooking.guest_email}</p>
                  <p className="text-neutral-400 text-sm">{selectedBooking.guest_phone}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-neutral-500">Amount</p>
                  <p className="text-white text-xl font-bold">KES {selectedBooking.total_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Booked On</p>
                  <p className="text-white">
                    {selectedBooking.created_at && format(parseISO(selectedBooking.created_at), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
              </div>

              {selectedBooking.checked_in_at ? (
                <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span>Checked in at {format(parseISO(selectedBooking.checked_in_at), "MMM d, h:mm a")}</span>
                </div>
              ) : selectedBooking.status === "confirmed" && (
                <Button
                  onClick={() => handleCheckIn(selectedBooking.id)}
                  className="w-full btn-empire"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Check In Guest
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
